import React, { Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { ProtectedRoute } from "@components/auth/ProtectedRoute";
import { PermissionRoute } from "@components/auth/PermissionRoute";
import { AnyPermissionRoute } from "@components/auth/AnyPermissionRoute";
import { OwnerLayout } from "@components/layout/OwnerLayout";
import { GlobalLoader } from "@components/loader";
import { NotFoundPage } from "@pages/NotFoundPage";
import { ASSOCIATE_DASHBOARD_PERMISSION } from "@lib/permissions";

const Login = React.lazy(() => import("@pages/Login").then((m) => ({ default: m.Login })));
const ForgotPassword = React.lazy(() =>
    import("@pages/ForgotPassword").then((m) => ({ default: m.ForgotPassword }))
);
const Otp = React.lazy(() => import("@pages/Otp").then((m) => ({ default: m.Otp })));
const ResetPassword = React.lazy(() =>
    import("@pages/ResetPassword").then((m) => ({ default: m.ResetPassword }))
);
const OverviewPage = React.lazy(() =>
    import("@pages/OverviewPage").then((m) => ({ default: m.OverviewPage }))
);
const AssociateDashboardPage = React.lazy(() =>
    import("@pages/AssociateDashboardPage").then((m) => ({ default: m.AssociateDashboardPage }))
);
const OwnersPage = React.lazy(() =>
    import("@pages/OwnersPage").then((m) => ({ default: m.OwnersPage }))
);
const ProfilePage = React.lazy(() =>
    import("@pages/ProfilePage").then((m) => ({ default: m.ProfilePage }))
);
const AssociatesPage = React.lazy(() =>
    import("@pages/AssociatesPage").then((m) => ({ default: m.AssociatesPage }))
);
const AssociateDetailPage = React.lazy(() =>
    import("@pages/AssociateDetailPage").then((m) => ({ default: m.AssociateDetailPage }))
);
const TenantsPage = React.lazy(() =>
    import("@pages/TenantsPage").then((m) => ({ default: m.TenantsPage }))
);
const TenantDetailPage = React.lazy(() =>
    import("@pages/TenantDetailPage").then((m) => ({ default: m.TenantDetailPage }))
);
const PaymentsPage = React.lazy(() =>
    import("@pages/PaymentsPage").then((m) => ({ default: m.PaymentsPage }))
);
const PlansPage = React.lazy(() =>
    import("@pages/PlansPage").then((m) => ({ default: m.PlansPage }))
);
const UserManagementPage = React.lazy(() =>
    import("@pages/UserManagementPage").then((m) => ({ default: m.UserManagementPage }))
);
const MonthlyReportPage = React.lazy(() =>
    import("@pages/MonthlyReportPage").then((m) => ({ default: m.MonthlyReportPage }))
);
const SupportTicketsPage = React.lazy(() =>
    import("@pages/SupportTicketsPage").then((m) => ({ default: m.SupportTicketsPage }))
);
const SupportTicketDetailPage = React.lazy(() =>
    import("@pages/SupportTicketDetailPage").then((m) => ({ default: m.SupportTicketDetailPage }))
);
const SettingsPage = React.lazy(() =>
    import("@pages/SettingsPage").then((m) => ({ default: m.SettingsPage }))
);
const RequestLogsPage = React.lazy(() =>
    import("@pages/RequestLogsPage").then((m) => ({ default: m.RequestLogsPage }))
);
const NotificationsPage = React.lazy(() =>
    import("@pages/NotificationsPage").then((m) => ({ default: m.NotificationsPage }))
);

export const Router: React.FC = () => (
    <Suspense fallback={<GlobalLoader />}>
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/otp" element={<Otp />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
                element={
                    <ProtectedRoute>
                        <OwnerLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<OverviewPage />} />
                <Route
                    path="associate/dashboard"
                    element={
                        <PermissionRoute permission={ASSOCIATE_DASHBOARD_PERMISSION} exact>
                            <AssociateDashboardPage />
                        </PermissionRoute>
                    }
                />
                <Route
                    path="owners"
                    element={
                        <PermissionRoute permission="platform.owners.create">
                            <OwnersPage />
                        </PermissionRoute>
                    }
                />
                <Route path="profile" element={<ProfilePage />} />
                <Route
                    path="associates"
                    element={
                        <PermissionRoute permission="platform.associates.read">
                            <AssociatesPage />
                        </PermissionRoute>
                    }
                />
                <Route
                    path="associates/:id"
                    element={
                        <PermissionRoute permission="platform.associates.read">
                            <AssociateDetailPage />
                        </PermissionRoute>
                    }
                />
                <Route
                    path="tenants"
                    element={
                        <PermissionRoute permission="platform.tenants.read">
                            <TenantsPage />
                        </PermissionRoute>
                    }
                />
                <Route
                    path="tenants/:id"
                    element={
                        <PermissionRoute permission="platform.tenants.read">
                            <TenantDetailPage />
                        </PermissionRoute>
                    }
                />
                <Route
                    path="payments"
                    element={
                        <PermissionRoute permission="platform.payments.read">
                            <PaymentsPage />
                        </PermissionRoute>
                    }
                />
                <Route
                    path="plans"
                    element={
                        <PermissionRoute permission="platform.plans.read">
                            <PlansPage />
                        </PermissionRoute>
                    }
                />
                <Route
                    path="users"
                    element={
                        <AnyPermissionRoute
                            permissions={["platform.staff.read", "platform.roles.read"]}
                        >
                            <UserManagementPage />
                        </AnyPermissionRoute>
                    }
                />
                <Route
                    path="reports/monthly"
                    element={
                        <PermissionRoute permission="platform.reports.read">
                            <MonthlyReportPage />
                        </PermissionRoute>
                    }
                />
                <Route
                    path="support/tickets"
                    element={
                        <PermissionRoute permission="support.tickets.read">
                            <SupportTicketsPage />
                        </PermissionRoute>
                    }
                />
                <Route
                    path="support/tickets/:id"
                    element={
                        <PermissionRoute permission="support.tickets.read">
                            <SupportTicketDetailPage />
                        </PermissionRoute>
                    }
                />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route
                    path="request-logs"
                    element={
                        <PermissionRoute permission="platform.analytics.read">
                            <RequestLogsPage />
                        </PermissionRoute>
                    }
                />
                <Route path="*" element={<NotFoundPage />} />
            </Route>
        </Routes>
    </Suspense>
);
